import { useEffect, useState, useRef, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import {
  fetchDashboardStats, fetchTopAtcCategories, fetchTopSuppliersInTopAtc,
  fetchTopSuppliersByInn, fetchLatestSuppliers,
  fetchSupplierRegions, fetchDossierAvailability,
  fetchContractAlerts, fetchMoleculesFewestSuppliers, fetchMoleculesMostSuppliers,
  getDashboardToken,
} from '../services/dashboardService';
import { fetchRegisterCounts } from '../services/externalRegisterService';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie, Legend, CartesianGrid, FunnelChart, Funnel, LabelList,
} from 'recharts';
import './Dashboard.css';

const TODO_API = import.meta.env.VITE_API_URL;

// Recharts animation config — consistent across all dashboard charts
const ANIM_DURATION = 700;
const ANIM_EASING = 'ease-out';

// ISO-2 → flag-icons CSS class (SVG flags via CDN, works on all OS/browsers)
function FlagIcon({ iso }) {
  if (!iso || iso.length !== 2) return null;
  return <span className={`fi fi-${iso.toLowerCase()}`} style={{ fontSize: '1.1em', verticalAlign: 'middle' }} />;
}

// Country name → ISO-2 (client-side mapping, covers common variants in the data)
const COUNTRY_ISO2_CLIENT = {
  'india': 'IN', 'germany': 'DE', 'united kingdom': 'GB', 'uk': 'GB',
  'great britain': 'GB', 'england': 'GB',
  'netherlands': 'NL', 'the netherlands': 'NL', 'holland': 'NL', 'france': 'FR',
  'spain': 'ES', 'italy': 'IT', 'portugal': 'PT', 'switzerland': 'CH',
  'austria': 'AT', 'belgium': 'BE', 'denmark': 'DK', 'sweden': 'SE',
  'norway': 'NO', 'finland': 'FI', 'ireland': 'IE', 'poland': 'PL',
  'czech republic': 'CZ', 'czechia': 'CZ', 'hungary': 'HU',
  'romania': 'RO', 'bulgaria': 'BG', 'croatia': 'HR', 'greece': 'GR',
  'cyprus': 'CY', 'malta': 'MT', 'slovenia': 'SI', 'slovakia': 'SK',
  'estonia': 'EE', 'latvia': 'LV', 'lithuania': 'LT', 'luxembourg': 'LU',
  'united states': 'US', 'united states of america': 'US', 'usa': 'US', 'us': 'US',
  'canada': 'CA', 'china': 'CN', "people's republic of china": 'CN',
  'japan': 'JP', 'south korea': 'KR', 'korea': 'KR', 'republic of korea': 'KR',
  'australia': 'AU',
  'brazil': 'BR', 'mexico': 'MX',
  'turkey': 'TR', 'türkiye': 'TR', 'turkiye': 'TR',
  'israel': 'IL', 'egypt': 'EG', 'south africa': 'ZA', 'pakistan': 'PK',
  'bangladesh': 'BD', 'indonesia': 'ID', 'malaysia': 'MY', 'singapore': 'SG',
  'thailand': 'TH', 'taiwan': 'TW', 'russia': 'RU', 'russian federation': 'RU',
  'ukraine': 'UA',
  'jordan': 'JO', 'iceland': 'IS', 'serbia': 'RS', 'iran': 'IR',
  'hong kong': 'HK', 'new zealand': 'NZ', 'saudi arabia': 'SA',
  'united arab emirates': 'AE', 'uae': 'AE', 'argentina': 'AR',
  'colombia': 'CO', 'chile': 'CL', 'peru': 'PE', 'venezuela': 'VE',
  'nigeria': 'NG', 'kenya': 'KE',
  'morocco': 'MA', 'tunisia': 'TN', 'algeria': 'DZ', 'sri lanka': 'LK',
  'north macedonia': 'MK', 'bosnia and herzegovina': 'BA', 'albania': 'AL',
  'liechtenstein': 'LI', 'monaco': 'MC', 'montenegro': 'ME', 'moldova': 'MD',
  'georgia': 'GE', 'armenia': 'AM', 'azerbaijan': 'AZ', 'kazakhstan': 'KZ',
  'philippines': 'PH', 'vietnam': 'VN', 'nepal': 'NP', 'myanmar': 'MM',
  'cuba': 'CU', 'costa rica': 'CR', 'puerto rico': 'PR',
};
function countryIso2(name) {
  if (!name) return null;
  return COUNTRY_ISO2_CLIENT[name.trim().toLowerCase()] || null;
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { instance: msalInstance } = useMsal();

  const [stats, setStats] = useState(null);
  const [statsErr, setStatsErr] = useState(false);
  const [extCounts, setExtCounts] = useState(null);
  const [todos, setTodos] = useState(null);
  const [todoPage, setTodoPage] = useState(1);

  const [chartAtc, setChartAtc] = useState({ data: null, error: false });
  const [chartSuppAtc, setChartSuppAtc] = useState({ data: null, error: false });
  const [chartInn, setChartInn] = useState({ data: null, error: false });
  const [chartLatest, setChartLatest] = useState({ data: null, error: false });
  const [chartRegions, setChartRegions] = useState({ data: null, error: false });
  const [chartDossier, setChartDossier] = useState({ data: null, error: false });
  const [chartScarce, setChartScarce] = useState({ data: null, error: false });
  const [chartWidely, setChartWidely] = useState({ data: null, error: false });
  const [contractAlerts, setContractAlerts] = useState(null);

  // Cached API-scoped access token (refreshed every ~10min, ~1h lifetime)
  const tokenRef = useRef(null);
  const refreshToken = useCallback(async () => {
    const t = await getDashboardToken(msalInstance);
    tokenRef.current = t;
    return t;
  }, [msalInstance]);

  // On mount: acquire token once, then load all dashboard data + start online-users poll
  useEffect(() => {
    let alive = true;

    (async () => {
      const token = await refreshToken();

      // ── Chart data (one-time load) ──
      fetchDashboardStats(token)
        .then(d => { if (alive) setStats(d); })
        .catch(() => { if (alive) setStatsErr(true); });
      fetchRegisterCounts()
        .then(d => { if (alive) setExtCounts(d); })
        .catch(() => {});
      // Todos use the same API-scoped token now
      (async () => {
        try {
          const headers = token ? { Authorization: `Bearer ${token}` } : {};
          const r = await fetch(`${TODO_API}/api/profile/todos`, { headers });
          if (!r.ok) throw new Error();
          const d = await r.json();
          if (alive) setTodos(d);
        } catch {}
      })();
      fetchTopAtcCategories(token)
        .then(d => { if (alive) setChartAtc({ data: d, error: false }); })
        .catch(() => { if (alive) setChartAtc({ data: null, error: true }); });
      fetchTopSuppliersInTopAtc(token)
        .then(d => { if (alive) setChartSuppAtc({ data: d, error: false }); })
        .catch(() => { if (alive) setChartSuppAtc({ data: null, error: true }); });
      fetchTopSuppliersByInn(token)
        .then(d => { if (alive) setChartInn({ data: d, error: false }); })
        .catch(() => { if (alive) setChartInn({ data: null, error: true }); });
      fetchLatestSuppliers(token)
        .then(d => { if (alive) setChartLatest({ data: d, error: false }); })
        .catch(() => { if (alive) setChartLatest({ data: null, error: true }); });
      fetchSupplierRegions(token)
        .then(d => { if (alive) setChartRegions({ data: d, error: false }); })
        .catch(() => { if (alive) setChartRegions({ data: null, error: true }); });
      fetchDossierAvailability(token)
        .then(d => { if (alive) setChartDossier({ data: d, error: false }); })
        .catch(() => { if (alive) setChartDossier({ data: null, error: true }); });
      fetchMoleculesFewestSuppliers(token)
        .then(d => { if (alive) setChartScarce({ data: d, error: false }); })
        .catch(() => { if (alive) setChartScarce({ data: null, error: true }); });
      fetchMoleculesMostSuppliers(token)
        .then(d => { if (alive) setChartWidely({ data: d, error: false }); })
        .catch(() => { if (alive) setChartWidely({ data: null, error: true }); });
      fetchContractAlerts(token)
        .then(d => { if (alive) setContractAlerts(d || []); })
        .catch(() => { if (alive) setContractAlerts([]); });

      // ── Refresh cached token every 10 min (well within ~1h lifetime) ──
      const tokenInterval = setInterval(refreshToken, 10 * 60 * 1000);

      // Store cleanup refs
      if (alive) {
        cleanupRef.current = () => {
          clearInterval(tokenInterval);
        };
      }
    })();

    return () => {
      alive = false;
      if (cleanupRef.current) cleanupRef.current();
    };
  }, [msalInstance, refreshToken]);

  const cleanupRef = useRef(null);

  const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : n);
  const renderValue = (n) => {
    if (statsErr) return '—';
    if (stats == null) return <span className="kpi-skel" aria-hidden="true" />;
    return fmt(n);
  };

  const newCount = stats?.newLast30Days;
  const productsTrend = (newCount && newCount > 0)
    ? `↑ +${fmt(newCount)} added in last 30 days`
    : 'No new products this month';
  const productsTrendCls = (newCount && newCount > 0) ? 'text-success' : 'text-muted';

  return (
    <div className="content db-page-bg">
      {/* ── KPI Row — UNTOUCHED ── */}
      <div className="row g-3" style={{ display: 'flex', alignItems: 'stretch' }}>
        {[
          { cls: 'c-teal', ico: <span style={{ fontSize: 22 }}>💊</span>, label: 'Total Products of Supplier', val: renderValue(stats?.totalSupplierProducts), trend: productsTrend, tCls: productsTrendCls, onClick: () => navigate('/supplier-products') },
          { cls: 'c-teal', ico: <span style={{ fontSize: 22 }}>🏭</span>, label: 'Total Suppliers', val: renderValue(stats?.totalSuppliers), trend: 'Unique suppliers in DB', tCls: 'text-muted', to: '/suppliers' },
        ].map((k, i) => {
          const inner = (
            <>
              <div className="kico">{k.ico}</div>
              <div className="klbl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', fontSize: '11px' }}>{k.label}</div>
              <div className="kval" style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, letterSpacing: '-1px', fontVariantNumeric: 'tabular-nums' }}>{k.val}</div>
              <div className={`ktr ${k.tCls}`} style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 500, fontSize: '12px' }}>{k.trend}</div>
            </>
          );
          return (
            <div className="col-xl col-lg-6 col-md-6 col-sm-12 d-flex" key={i}>
              {k.onClick
                ? (
                  <a href="#" onClick={(e) => { e.preventDefault(); k.onClick(); }} className={`kpi ${k.cls} d-block text-decoration-none`} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{inner}</a>
                )
                : (
                  <Link to={k.to} className={`kpi ${k.cls} d-block text-decoration-none`} style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>{inner}</Link>
                )}
            </div>
          );
        })}
        {/* External Data KPI Card — UNTOUCHED */}
        <div className="col-xl col-lg-6 col-md-6 col-sm-12 d-flex">
          <div className="kpi c-teal d-block" style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div className="kico"><span style={{ fontSize: 22 }}>🌐</span></div>
            <div className="klbl" style={{ fontFamily: "'DM Sans', sans-serif", fontWeight: 600, letterSpacing: '0.8px', textTransform: 'uppercase', fontSize: '11px' }}>External Data of Suppliers</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 5, marginTop: 8, fontSize: 12.5, color: 'var(--navy)' }}>
              {[
                { label: '🇪🇺 EMA Register', path: '/ema-supps', count: extCounts?.ema },
                { label: '🇳🇱 Dutch CBG-MEB', path: '/dutch-supps', count: extCounts?.dutch },
                { label: '🇫🇷 ANSM France', path: '/ansm-supps', count: extCounts?.ansm },
                { label: '🌐 HMA (MRI Product Index)', path: '/hma-supps', count: extCounts?.hma },
              ].map(r => (
                <div key={r.path} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <span role="button" tabIndex={0} onClick={() => navigate(r.path)} onKeyDown={(e) => { if (e.key === 'Enter') navigate(r.path); }} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: 'var(--teal)', textDecoration: 'none' }}>{r.label}</span>
                  </span>
                  <strong>{r.count != null ? fmt(r.count) : '—'}</strong>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* ── To-Do List Card — UNTOUCHED ── */}
      <div className="row g-3" style={{ marginTop: 4 }}>
        <div className="col-12">
          <div className="kpi c-teal d-block" style={{ display: 'flex', flexDirection: 'column', padding: 0, overflow: 'hidden' }}>
            <div style={{ textAlign: 'center', padding: '20px 16px 12px' }}>
              <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--navy)', fontFamily: "'DM Sans', sans-serif" }}>📋 My To-Do</div>
            </div>
            <div style={{ display: 'flex', flexDirection: 'row', flex: 1 }}>
              <div style={{ width: '30%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
                <div style={{ fontFamily: "'Inter', sans-serif", fontWeight: 800, fontSize: 56, letterSpacing: '-2px', fontVariantNumeric: 'tabular-nums', color: 'var(--navy)', lineHeight: 1 }}>
                  {todos == null ? <span className="kpi-skel" aria-hidden="true" /> : fmt(todos.total_count)}
                </div>
              </div>
              <div style={{ width: '70%', display: 'flex', flexDirection: 'column' }}>
                {todos == null ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)', fontSize: 14, padding: 24 }}>Loading...</div>
                ) : todos.total_count === 0 ? (
                  <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--t3)', fontSize: 14, padding: 24 }}>All caught up! There is no to-do. Enjoy!</div>
                ) : (() => {
                  const TODO_PAGE = 5;
                  const todoRows = todos.rows || [];
                  const todoPgCount = Math.max(1, Math.ceil(todoRows.length / TODO_PAGE));
                  const safeTodoPg = Math.min(todoPage, todoPgCount);
                  const todoStart = (safeTodoPg - 1) * TODO_PAGE;
                  const todoSlice = todoRows.slice(todoStart, todoStart + TODO_PAGE);
                  return (
                    <>
                      <div style={{ flex: 1, overflowX: 'auto', padding: '4px 0' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <thead><tr>
                            <th style={{ width: 50, padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Task</th>
                            <th style={{ width: 80, padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Action</th>
                            <th style={{ padding: '8px 10px', textAlign: 'left', fontWeight: 700, color: 'var(--navy)', fontSize: 14 }}>Description</th>
                          </tr></thead>
                          <tbody>
                            {todoSlice.map((row, idx) => {
                              const num = todoStart + idx + 1;
                              const desc = row.status_type === 'Pending'
                                ? `There are ${fmt(row.product_count)} supplier products in Pending status from ${row.supplier_name}. Please assign the proper status.`
                                : `There are ${fmt(row.product_count)} uncategorized supplier products from ${row.supplier_name}. Please assign the proper status.`;
                              const statusParam = row.status_type === 'Pending' ? 'Pending' : 'No Status';
                              const params = new URLSearchParams();
                              params.set('supplier', row.supplier_name);
                              params.set('product_status', statusParam);
                              return (
                                <tr key={`${row.supplier_name}-${row.status_type}`} style={{ transition: 'background 0.15s' }} onMouseEnter={e => { for (const td of e.currentTarget.children) td.style.background = '#EFF8FF'; }} onMouseLeave={e => { for (const td of e.currentTarget.children) td.style.background = ''; }}>
                                  <td style={{ padding: '8px 10px', fontWeight: 600, color: 'var(--navy)', fontSize: 15 }}>{num}</td>
                                  <td style={{ padding: '8px 10px' }}>
                                    <button type="button" style={{ border: '1px solid #dee2e6', borderRadius: 6, padding: '3px 7px', background: 'white', cursor: 'pointer', lineHeight: 1 }} title="Open in supplier products" onClick={() => navigate(`/supplier-products?${params.toString()}`)}>
                                      <i className="bi bi-box-arrow-up-right" style={{ color: 'var(--navy)', fontSize: 14 }}></i>
                                    </button>
                                  </td>
                                  <td style={{ padding: '8px 10px', color: 'var(--navy)', fontSize: 15 }}>{desc}</td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                      {todoPgCount > 1 && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 12px' }}>
                          <button type="button" disabled={safeTodoPg === 1} onClick={() => setTodoPage(p => Math.max(1, p - 1))} style={{ fontSize: 11, color: safeTodoPg === 1 ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safeTodoPg === 1 ? '#dee2e6' : 'var(--teal)'), background: 'white', borderRadius: 5, padding: '4px 8px', cursor: safeTodoPg === 1 ? 'not-allowed' : 'pointer' }}>Prev</button>
                          <span style={{ fontSize: 11, color: 'var(--t3)' }}>{safeTodoPg} / {todoPgCount}</span>
                          <button type="button" disabled={safeTodoPg === todoPgCount} onClick={() => setTodoPage(p => Math.min(todoPgCount, p + 1))} style={{ fontSize: 11, color: safeTodoPg === todoPgCount ? 'var(--t3)' : 'var(--teal)', border: '1px solid ' + (safeTodoPg === todoPgCount ? '#dee2e6' : 'var(--teal)'), background: 'white', borderRadius: 5, padding: '4px 8px', cursor: safeTodoPg === todoPgCount ? 'not-allowed' : 'pointer' }}>Next</button>
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════
          Unified 3×3 chart grid — 9 cards in ONE grid container
          Row 1: Therapeutic Areas | Leading Suppliers | Molecule Breadth
          Row 2: Dossier Availability | Scarce Molecules | Widely Sourced
          Row 3: Supplier Regions | Most Recently Updated | Online Users
          ══════════════════════════════════════════════════════════════ */}
      <div className="db-chart-grid" style={{ marginTop: 4, marginBottom: 16 }}>

        {/* ── R1C1: Top 10 Therapeutic Areas — VERTICAL BARS ── */}
        <DbChartCard title="Top 10 Therapeutic Areas by Product Count" icon="📊" accent="navy" delay={1} state={chartAtc}>
          {(data) => (
            <ResponsiveContainer width="100%" height={380}>
              <BarChart data={data} margin={{ top: 10, right: 10, bottom: 60, left: 10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eef0f4" vertical={false} />
                <XAxis dataKey="category" tick={{ fontSize: 9, fill: '#0E2841' }} tickLine={false} axisLine={{ stroke: '#e9ecf1' }} angle={-40} textAnchor="end" interval={0} tickFormatter={(v) => truncate(v, 14)} />
                <YAxis tick={{ fontSize: 10, fill: '#8c95a4' }} tickLine={false} axisLine={false} />
                <Tooltip formatter={(val) => [val.toLocaleString(), 'Products']} contentStyle={{ fontSize: 13, borderRadius: 8 }} />
                <Bar dataKey="count" radius={[4, 4, 0, 0]} barSize={22} animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING} style={{ cursor: 'pointer' }} onClick={(entry) => navigate(`/supplier-products?atc_category=${encodeURIComponent(entry.category)}`)}>
                  {data.map((_, i) => <Cell key={i} fill={PALETTE_NAVY[i % PALETTE_NAVY.length]} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
        </DbChartCard>

        {/* ── R1C2: Leading Suppliers — FLAG + BAR REFERRER LIST ── */}
        <DbChartCard title="Leading Suppliers in Top Therapeutic Areas" icon="🏢" accent="teal" delay={2} state={chartSuppAtc}>
          {(data) => {
            const total = data.reduce((s, d) => s + d.count, 0);
            const maxVal = data.length > 0 ? data[0].count : 1;
            return (
              <div style={{ overflowY: 'auto', maxHeight: 380 }}>
                <ul className="db-region-list">
                  {data.map((d, i) => {
                    const pct = total > 0 ? ((d.count / total) * 100).toFixed(1) : '0.0';
                    const iso = countryIso2(d.hq_country);
                    return (
                      <li key={d.supplier_name} className="db-referrer-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/supplier-products?supplier=${encodeURIComponent(d.supplier_name)}`)}>
                        <span className="db-referrer-rank">{i + 1}</span>
                        <span className="db-referrer-flag"><FlagIcon iso={iso} /></span>
                        <span className="db-referrer-name">{truncate(d.supplier_name, 24)}</span>
                        <span className="db-referrer-bar-wrap">
                          <span className="db-referrer-bar" style={{ width: `${(d.count / maxVal) * 100}%` }} />
                        </span>
                        <span className="db-referrer-value">{fmt(d.count)} <span className="db-referrer-pct">({pct}%)</span></span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }}
        </DbChartCard>

        {/* ── R1C3: Molecule Breadth — MULTI-COLOR FUNNEL ── */}
        <DbChartCard title="Top 10 Suppliers by Molecule Breadth (Distinct INNs)" icon="🧬" accent="teal" delay={3} state={chartInn}>
          {(data) => {
            const funnelData = data.map((d, i) => ({
              ...d,
              name: `${d.inn_count} - ${d.supplier_name}`,
              value: d.inn_count,
              fill: PALETTE_FUNNEL[i % PALETTE_FUNNEL.length],
              _textFill: funnelTextColor(PALETTE_FUNNEL[i % PALETTE_FUNNEL.length]),
            }));
            return (
              <ResponsiveContainer width="100%" height={380}>
                <FunnelChart>
                  <Tooltip formatter={(val, name) => [val.toLocaleString(), name]} contentStyle={{ fontSize: 13, borderRadius: 8 }} />
                  <Funnel dataKey="value" data={funnelData} isAnimationActive animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING} onClick={(entry) => navigate(`/supplier-products?supplier=${encodeURIComponent(entry.supplier_name)}`)} style={{ cursor: 'pointer' }}>
                    <LabelList dataKey="name" position="center" fontSize={9} fontWeight={600} content={({ x, y, width, height, index }) => {
                      const item = funnelData[index];
                      if (!item) return null;
                      return <text x={x + width / 2} y={y + height / 2} textAnchor="middle" dominantBaseline="central" fill={item._textFill} fontSize={9} fontWeight={600}>{item.name}</text>;
                    }} />
                  </Funnel>
                </FunnelChart>
              </ResponsiveContainer>
            );
          }}
        </DbChartCard>

        {/* ── R2C1: Dossier Availability — DONUT ── */}
        <DbChartCard title="Dossier Availability" icon="📋" accent="orange" delay={4} state={chartDossier} caption="EU/NL/non-EU derived from supplier HQ and NL market availability. Counting active products.">
          {(data) => {
            const total = data.reduce((s, d) => s + d.count, 0);
            return (
              <ResponsiveContainer width="100%" height={320}>
                <PieChart>
                  <Pie data={data} dataKey="count" nameKey="region" cx="50%" cy="50%" innerRadius={50} outerRadius={90} paddingAngle={3} animationDuration={ANIM_DURATION} animationEasing={ANIM_EASING}>
                    {data.map((d, i) => <Cell key={i} fill={DOSSIER_COLORS[d.region] || '#8c95a4'} />)}
                  </Pie>
                  <Tooltip formatter={(val, name) => [val.toLocaleString(), name]} contentStyle={{ fontSize: 13, borderRadius: 8 }} />
                  <Legend layout="vertical" align="right" verticalAlign="middle" formatter={(v) => {
                    const d = data.find(x => x.region === v);
                    const pct = total > 0 ? ((d?.count || 0) / total * 100).toFixed(1) : '0';
                    return `${v}: ${fmt(d?.count || 0)} (${pct}%)`;
                  }} wrapperStyle={{ fontSize: 12, lineHeight: '22px' }} />
                </PieChart>
              </ResponsiveContainer>
            );
          }}
        </DbChartCard>

        {/* ── R2C2: Single-Source & Scarce Molecules — RANKED LIST ── */}
        <DbChartCard title="Single-Source & Scarce Molecules" icon="⚠️" accent="orange" delay={5} state={chartScarce}>
          {(data) => {
            const maxVal = data.length > 0 ? Math.max(...data.map(d => d.supplier_count)) : 1;
            return (
              <div style={{ overflowY: 'auto', maxHeight: 380 }}>
                <ul className="db-region-list">
                  {data.map((d, i) => (
                    <li key={d.inn} className="db-referrer-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/supplier-products?inn=${encodeURIComponent(d.inn)}`)}>
                      <span className="db-referrer-rank">{i + 1}</span>
                      <span className="db-referrer-name">
                        {truncate(d.inn, 22)}
                        {d.top_form && d.top_form !== '—' && <span title={d.top_form} style={{ marginLeft: 4, fontSize: '0.8em', color: '#999', fontWeight: 400 }}>{truncate(d.top_form, 12)}</span>}
                      </span>
                      <span className="db-referrer-bar-wrap">
                        <span className="db-referrer-bar" style={{ width: `${(d.supplier_count / maxVal) * 100}%`, background: '#E97132' }} />
                      </span>
                      <span className="db-referrer-value" style={{ color: '#E97132' }}>{d.supplier_count} <span className="db-referrer-pct">supplier{d.supplier_count !== 1 ? 's' : ''}</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }}
        </DbChartCard>

        {/* ── R2C3: Most Widely Sourced Molecules — RANKED LIST ── */}
        <DbChartCard title="Most Widely Sourced Molecules" icon="🌐" accent="navy" delay={6} state={chartWidely}>
          {(data) => {
            const maxVal = data.length > 0 ? data[0].supplier_count : 1;
            return (
              <div style={{ overflowY: 'auto', maxHeight: 380 }}>
                <ul className="db-region-list">
                  {data.map((d, i) => (
                    <li key={d.inn} className="db-referrer-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/supplier-products?inn=${encodeURIComponent(d.inn)}`)}>
                      <span className="db-referrer-rank">{i + 1}</span>
                      <span className="db-referrer-name">
                        {truncate(d.inn, 22)}
                        {d.top_form && d.top_form !== '—' && <span title={d.top_form} style={{ marginLeft: 4, fontSize: '0.8em', color: '#999', fontWeight: 400 }}>{truncate(d.top_form, 12)}</span>}
                      </span>
                      <span className="db-referrer-bar-wrap">
                        <span className="db-referrer-bar" style={{ width: `${(d.supplier_count / maxVal) * 100}%` }} />
                      </span>
                      <span className="db-referrer-value">{d.supplier_count} <span className="db-referrer-pct">suppliers</span></span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          }}
        </DbChartCard>

        {/* ── R3C1: Supplier Regions — TOP 10 FLAG LIST ── */}
        <DbChartCard title="Supplier Regions" icon="🗺️" accent="teal" delay={4} state={chartRegions}>
          {(data) => (
            <div style={{ overflowY: 'auto', maxHeight: 380 }}>
              <ul className="db-region-list">
                {data.map((r, i) => {
                  const iso = r.iso2 || countryIso2(r.country);
                  return (
                    <li key={r.country} className="db-flag-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/supplier-products?hq_country=${encodeURIComponent(r.country)}`)}>
                      <span className="db-flag-rank">{i + 1}</span>
                      <span className="db-flag-emoji"><FlagIcon iso={iso} /></span>
                      <span className="db-flag-name">{r.country}</span>
                      <span className="db-flag-dash">—</span>
                      <span className="db-flag-count">{r.count}</span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </DbChartCard>

        {/* ── R3C2: Most Recently Updated — PROGRESS LIST ── */}
        <DbChartCard title="Most Recently Updated Suppliers" icon="🕐" accent="orange" delay={5} state={chartLatest}>
          {(data) => {
            const now = Date.now();
            const withDays = data.map(d => {
              const dt = new Date(d.latest_date);
              return { ...d, _daysAgo: Math.max(0, Math.round((now - dt.getTime()) / 86400000)), _label: dt.toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) };
            });
            const maxRecency = Math.max(...withDays.map(d => d._daysAgo), 1);
            return (
              <div style={{ overflowY: 'auto', maxHeight: 380 }}>
                <ul className="db-progress-list">
                  {withDays.map((d, i) => {
                    const pct = maxRecency > 0 ? Math.max(8, ((maxRecency - d._daysAgo) / maxRecency) * 100) : 100;
                    return (
                      <li key={d.supplier_name} className="db-progress-item" style={{ cursor: 'pointer' }} onClick={() => navigate(`/supplier-products?supplier=${encodeURIComponent(d.supplier_name)}`)}>
                        <div className="db-progress-top">
                          <span className="db-progress-name">{truncate(d.supplier_name, 26)}</span>
                          <span className="db-progress-value">{d._label}</span>
                        </div>
                        <div className="db-progress-track">
                          <div className="db-progress-fill" style={{ width: `${pct}%`, background: PALETTE_BROWN[i % PALETTE_BROWN.length] }} />
                        </div>
                        <div className="db-progress-sub">{d._daysAgo === 0 ? 'Today' : `${d._daysAgo} day${d._daysAgo !== 1 ? 's' : ''} ago`}</div>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          }}
        </DbChartCard>

        {/* ── R3C3: Contract Alert ── */}
        <div className={`db-chart-card accent-orange db-chart-enter db-delay-6`}>
          <div className="db-chart-header">
            <div className="db-chart-icon bg-orange">📋</div>
            <div className="db-chart-title">Contract Alert</div>
          </div>
          <div className="db-chart-body">
            {contractAlerts == null ? (
              <div className="db-chart-placeholder">Loading...</div>
            ) : contractAlerts.length === 0 ? (
              <div className="db-chart-placeholder" style={{ color: '#30BF82' }}>No contracts expiring soon.</div>
            ) : (
              <div style={{ overflowY: 'auto', maxHeight: 380 }}>
                <ul className="db-region-list">
                  {contractAlerts.map((c, i) => {
                    const endDt = c.end_date ? new Date(c.end_date).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';
                    return (
                      <li key={`${c.supplier_id}-${c.end_date}-${i}`} className="db-referrer-item" style={{ alignItems: 'flex-start', gap: 6, cursor: 'pointer' }} onClick={() => navigate('/supplier-contracts')}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ fontWeight: 600, fontSize: 13 }}>{truncate(c.supplier_name || '—', 26)}</div>
                          <div style={{ fontSize: 11, color: '#888', marginTop: 1 }}>{endDt}{c.contact_name ? ` · ${c.contact_name}` : ''}</div>
                        </div>
                        {c.status === 'expired' ? (
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#E53935', borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap' }}>Expired</span>
                        ) : (
                          <span style={{ fontSize: 11, fontWeight: 600, color: '#fff', background: '#E97132', borderRadius: 4, padding: '2px 7px', whiteSpace: 'nowrap' }}>{c.days_left}d left</span>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Palettes ──
const PALETTE_NAVY = ['#0B3D6B','#0E4A7F','#115893','#1466A7','#1774BB','#1A82CF','#1E8FE0','#239CE8','#2EAAEF','#40B5F3'];
const PALETTE_BROWN = ['#8B4513','#9B5319','#AB611F','#BB6F25','#CB7D2B','#DB8B31','#E49937','#EDA73D','#F2B54E','#F7C360'];
const PALETTE_FUNNEL = ['#0E2841','#156082','#1A82CF','#E97132','#2EAA6F','#9C27B0','#00838F','#F9A825','#5C6BC0','#EF5350'];
const DOSSIER_COLORS = { NL: '#E97132', EU: '#156082', 'non-EU': '#0E2841' };

const truncate = (s, max = 22) => s && s.length > max ? s.slice(0, max) + '\u2026' : s;
const fmt = (n) => (typeof n === 'number' ? n.toLocaleString('en-US') : n);

function funnelTextColor(hex) {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16) / 255;
  const g = parseInt(c.substring(2, 4), 16) / 255;
  const b = parseInt(c.substring(4, 6), 16) / 255;
  const lum = 0.2126 * r + 0.7152 * g + 0.0722 * b;
  return lum > 0.45 ? '#0E2841' : '#FFFFFF';
}

function DbChartCard({ title, icon, accent, delay, state, caption, children }) {
  const { data, error } = state;
  return (
    <div className={`db-chart-card accent-${accent} db-chart-enter db-delay-${delay}`}>
      <div className="db-chart-header">
        <div className={`db-chart-icon bg-${accent}`}>{icon}</div>
        <div className="db-chart-title">{title}</div>
      </div>
      {error ? (
        <div className="db-chart-placeholder db-chart-error">Failed to load data</div>
      ) : !data ? (
        <div className="db-chart-placeholder">Loading...</div>
      ) : data.length === 0 ? (
        <div className="db-chart-placeholder">No data</div>
      ) : (
        <div className="db-chart-body">{children(data)}</div>
      )}
      {caption && <div className="db-chart-caption">{caption}</div>}
    </div>
  );
}
